import React, { Component } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import { injectIntl, defineMessages, FormattedMessage, FormattedDate } from 'react-intl';
import PropTypes from 'prop-types';
import OutsideClickHandler from 'react-outside-click-handler';
import intlShape from '@/shared/utils/intlShape';
import FileViewer from '@/shared/components/FileViewers/FileViewer';
import SimpleFileUpload from '@/shared/components/input/SimpleFileUpload';
import Button from '@/shared/components/Button';
import moment from 'moment';
import classNames from 'classnames';
import debounce from 'lodash-es/debounce';
import isEqual from 'lodash-es/isEqual';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import { FaInfoCircle, FaLink, FaFilePdf } from 'react-icons/fa';
import { MdReply, MdReplyAll } from 'react-icons/md';
import api from '@/shared/api';
import ShareItemModal from '@/shared/components/ShareItemModal';

import LoadingAnimation from '@/shared/components/LoadingAnimation';
import FormattedListValue, {
  listItemMsg,
  formatListValue,
} from '@/shared/components/FormattedListValue';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import enumCache from '@/shared/services/EnumCacheService';
import FormattedDateTime from '@/shared/components/FormattedDateTime';
import YesNoModal from '@/shared/components/YesNoModal';
import Checkbox from '@/shared/components/input/Checkbox';
import authService, { functionalities, privileges } from '@/shared/services/AuthService';
import ForwardEmailModal from '@/shared/components/email/ForwardEmailModal';
import {
  isBeforeMinimum,
  isValidNumber,
  validateMinimumDate,
} from '@/shared/utils/validationHelpers';
import DataCacheService from '@/shared/services/DataCacheService';

import {
  AddIcon,
  DeleteIcon,
  DownloadIcon,
  IconPencil,
  LeftArrowIcon,
  ReplaceIcon,
  RightArrowIcon,
  SaveIcon,
  ShareIcon,
  UndoIcon,
  SpinnerIcon,
} from '@/shared/components/Icons';

import FileIcon from '@/shared/components/FileIcon';
import DatePicker from '@/shared/components/input/DatePicker';
import Tag from '@/shared/components/Tag';
import DocumentService from '@/shared/services/DocumentService';
import handleHttpError from '@/shared/services/HandleHttpError';
import RelationListPicker from '@/shared/components/input/RelationListPicker';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import MultiSelect from '@/shared/components/input/multiselect/MultiSelect';
import ReplyToEmailModal from '@/shared/components/email/ReplyToEmailModal';
import { getRelationName } from '@/shared/components/RelationName';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import TaskDetailsModal from '../../tasks/taskDetails/TaskDetailsModal';
import EmailMetadataDetails from './EmailMetadataDetails';
import config from '../../../config';
import TaskStatusToggle from '../../tasks/TaskStatusToggle';
import TaskIcon from '../../tasks/TaskIcon';
import CreateToDoModal from '../../tasks/CreateToDoModal';
import TasksForMultipleRelationsSelector from '../../../shared/components/input/TasksForMultipleRelationsSelector';
import FormattedDateShort from '../../../shared/components/FormattedDateShort';
import { DocumentAudits } from './DocumentAudits';
import documentMessages from '../documentMessages';

const dontShowAgainStorageKeyDocuments = 'DocumentsAIExtractionWarningDontShowAgain';

const messages = defineMessages({
  AuditLogsForDocument: 'Audit history for {name}',
  HideAuditLogs: 'Hide audit logs',
  ShowAuditLogs: 'Show audit logs',
  ResendEmail: 'Resend email',
  ResendEmailConfirmationMsg: 'Are you sure you want to resend this email?',
  Save: 'Save',
  Cancel: 'Cancel',
  DocumentDate: 'Document date',
  CreationDate: 'Creation date',
  DocumentCreatedBy: 'Created by',
  DeleteDocument: 'Delete document',
  StartTypingToSearch: 'Start typing to search',
  Success: 'Success',
  Tags: 'Tags',
  Download: 'Download',
  Edit: 'Edit',
  CheckoutBrowser: 'Checkout in browser',
  CheckoutApp: 'Checkout in app',
  Checkin: 'Checkin',
  DiscardCheckout: 'Discard checkout',
  ContinueEditing: 'Continue editing',
  PreviousDocument: 'Previous document',
  NextDocument: 'Next document',
  Open: 'Open',
  EnterTag: 'Enter tag',
  Filename: 'Filename',
  Relation: 'Relation',
  Relations: 'Relations',
  AddRelation: 'Add relation',
  RemoveRelation: 'Remove relation',
  ScannedDocument: 'Scanned document',
  DocumentType: 'Document type',
  Status: 'Status',
  Signature: 'Signature',
  ViewTask: 'View task',
  Tasks: 'Tasks',
  TheMailHasBeenSentAgain: 'The mail has been sent again',
  DeleteAttachment: 'Delete attachment',
  DeleteAttachmentConfirmationMsg: 'Are you sure you want to delete this attachment?',
  IsPublicOnPortal: 'Is public on portal',
  CreateNewToDo: 'Create new to-do',
  DocumentIsPublicOnPortalExplanation: 'This document can be accessed by 3rd party integrations.',
  Share: 'Share',
  LinkToTask: 'Link to task',
  DeleteTaskLink: 'Delete task link',
  DeleteTaskLinkConfirmationMsg:
    'Are you sure you want to remove the link between this document and this task?',
  DownloadAsPdf: 'Download as pdf',
  Reply: 'Reply',
  ReplyAll: 'ReplyAll',
  FileUnauthorized:
    'You do not have permission to view this file or your Sharepoint connection has not been configured yet.',
  ForceRelationModeInfo: 'It is required by your company to link documents to a relation',
  CheckedOutReminderModeInfo:
    'This document has been checked out for more than a day. Please review if it should be checked in.',
  Replace: 'Replace',
  MsgCheckinDocumentError: 'Error while checking in document',
  CheckinDocumentConfirmationMsg:
    'Are you sure you want to discard this checkout? It is currently checked out by {name}, this action may result in a loss of their changes.',
  CheckedOutSuccesfull: 'Successfully checked out file.',
  CheckedinSuccesfull: 'Successfully checked in file.',
  DiscardCheckoutSuccesfull: 'Successfully discarded checkout.',
  ExtractDocumentData: 'Extract data (AI)',
  ExtractingDocumentData: 'Extracting data...',
  ExtractionInProgress: 'Data extraction in progress',
  DocumentDataExtracted: 'Data extracted successfully',
  DocumentDataExtractionFailed: 'Failed to extract data',
  DocumentDataExtractionStarted: 'Data extraction started',
  NoRelationForExtraction: 'Please link a relation to this document before extracting data',
});

class DocumentDetailsModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    intl: intlShape.isRequired,
    allowNavigation: PropTypes.bool,
    forceRelationMode: PropTypes.bool,
    onReplaceDocument: PropTypes.func.isRequired,
    checkedoutReminderMode: PropTypes.bool,
  };

  static defaultProps = {
    show: false,
    disabled: false,
    allowNavigation: true,
    forceRelationMode: false,
    checkedoutReminderMode: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      newTag: '',
      document: props.document,
      documentId: props.documentId,
      documentLink: undefined,
      documentDetails: {},
      initialDocumentDetails: {},
      scannedDocument: undefined,
      tags: null,
      showCreateTodoModal: false,
      documentTypes: [],
      fullScreen: false,
      showValidation: false,
      addTasks: false,
      deleteTaskLink: null,
      unauthorized: false,
      extractingData: false,
      showExtractDataConfirmModal: false,
    };
  }

  componentDidMount() {
    const { document, documentId } = this.state;
    this.getDocumentDetails(documentId || document.id);
    this.getAvailableTags();

    SignalRService.subscribe('documentContentUpdated', this.getDocumentLink);
    SignalRService.subscribe('documentExtractionCompleted', this.onExtractionCompleted);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('documentContentUpdated', this.getDocumentLink);
    SignalRService.unsubscribe('documentExtractionCompleted', this.onExtractionCompleted);
  }

  onExtractionCompleted = result => {
    const { intl } = this.props;
    const { document } = this.state;

    if (result.documentId !== document.id) return;

    this.setState({ extractingData: false });

    if (result.success) {
      if (result.code === 18002) {
        ToastrService.info(intl.formatMessage(messages.DocumentDataExtractionInsufficientData));
      } else if (result.code === 18003) {
        ToastrService.info(intl.formatMessage(messages.DocumentDataExtractionUnsupportedType));
      } else {
        ToastrService.success(intl.formatMessage(messages.DocumentDataExtracted));
      }
    } else {
      const message = intl.formatMessage({
        id: `exceptioncode_${result.code}`.toLowerCase(),
        defaultMessage: intl.formatMessage(messages.DocumentDataExtractionFailed),
      });
      ToastrService.error(message);
    }
  };

  extractDocumentData = () => {
    const { intl } = this.props;
    const { documentDetails } = this.state;

    if (!documentDetails.relations || documentDetails.relations.length === 0) {
      ToastrService.warning(intl.formatMessage(messages.NoRelationForExtraction));
      return;
    }

    const skipConfirmation = localStorage.getItem(dontShowAgainStorageKeyDocuments) === 'true';

    if (skipConfirmation) {
      this.performDocumentExtraction();
    } else {
      this.setState({
        showExtractDataConfirmModal: true,
      });
    }
  };

  hasUnsavedChanges = () => {
    const { documentDetails, initialDocumentDetails } = this.state;
    if (!documentDetails || !initialDocumentDetails) return false;

    if (documentDetails.documentType !== initialDocumentDetails.documentType) return true;

    const currentRelationIds = (documentDetails.relations || []).map(x => x.id).sort();
    if (!isEqual(currentRelationIds, initialDocumentDetails.relations)) return true;

    return false;
  };

  performDocumentExtraction = () => {
    const { intl, onSave } = this.props;
    const { document } = this.state;

    this.setState({ extractingData: true });

    const triggerExtractionAndClose = savedData => {
      DocumentService.extractDocumentData(document.id).then(
        () => {
          ToastrService.info(intl.formatMessage(messages.DocumentDataExtractionStarted));
          onSave({ ...(savedData || document), extracting: true });
        },
        error => {
          this.setState({ extractingData: false });
          handleHttpError(intl)(error);
        },
      );
    };

    if (this.hasUnsavedChanges()) {
      this.saveDocumentDetails().then(
        data => triggerExtractionAndClose(data),
        () => this.setState({ extractingData: false }),
      );
    } else {
      triggerExtractionAndClose(null);
    }
  };

  confirmExtractDataWithAI = (dontShowAgain = false) => {
    if (dontShowAgain) {
      localStorage.setItem(dontShowAgainStorageKeyDocuments, 'true');
    }

    this.setState({ showExtractDataConfirmModal: false });
    this.performDocumentExtraction();
  };

  cancelExtractDataWithAI = () => {
    this.setState({
      showExtractDataConfirmModal: false,
    });
  };

  getDocumentLink = documentId => {
    const { intl } = this.props;
    const { document } = this.state;
    if (documentId !== document.id) return;

    this.setState({ loading: true });

    if (document.storageType === 1) {
      this.setState({ documentLink: document.path, loading: false });
    } else {
      api.base.get(`${config.appUrls.documents}/api/documents/OpenDocumentLink/${documentId}`).then(
        ({ data }) => {
          this.setState({ documentLink: data, loading: false });
        },
        error => {
          this.setState({ loading: false, documentLink: null });
          handleHttpError(intl)(error);
        },
      );
    }
  };

  loadScannedDocument = documentId => {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.documents}/api/documents/info/${documentId}`).then(
      ({ data }) => {
        this.setState({ scannedDocument: data });
      },
      error => {
        this.setState({ loading: false, documentLink: null });
        handleHttpError(intl)(error);
      },
    );
  };

  getDocumentDetails = documentId => {
    const { intl } = this.props;
    const documentTypesPromise = enumCache.getTranslated('documentType', intl, true);

    const documentDetailsPromise = api.base.get(
      `${config.appUrls.documents}/api/documents/details/${documentId}`,
    );

    const getDocumentLink = async () => {
      try {
        const response = await api.base.get(
          `${config.appUrls.documents}/api/documents/OpenDocumentLink/${documentId}`,
        );

        return response;
      } catch {
        this.setState({ unauthorized: true });
        return { data: null };
      }
    };

    this.setState({ loading: true });
    Promise.all([
      documentDetailsPromise,
      documentTypesPromise,
      getDocumentLink(),
      DocumentService.getWithoutRelationDocumentTypes(),
    ]).then(([{ data }, documentTypes, { data: documentLink }, withoutRelationDocumentTypes]) => {
      const selectedDocumentType = documentTypes.find(x => x.id === data.documentType);

      const creationUser = data.creationUserId
        ? DataCacheService.getUserName(data.creationUserId)
        : null;
      const showRelations = withoutRelationDocumentTypes.indexOf(data.documentType) < 0;
      this.setState(
        {
          loading: false,
          documentLink,
          document: data,
          documentTypes: documentTypes
            .map(x => ({
              ...x,
              key: x.id,
            }))
            .sort(dynamicSort('label')),
          selectedDocumentType: selectedDocumentType
            ? { key: selectedDocumentType.id, label: selectedDocumentType.label }
            : null,
          documentDetails: {
            ...data,
            documentDate: moment.utc(data.documentDate),
            creationDate: moment.utc(data.creationDate),
            creationUser,
            relations: data.relations || [],
            signingStatus: data.signingStatus === 21 ? 20 : data.signingStatus, // map "signingStatusCompletedOriginal"
            tags:
              data.tags &&
              data.tags.map(x => ({
                ...x,
                label: x.name,
              })),
          },
          initialDocumentDetails: {
            name: data.name,
            documentDate: moment.utc(data.documentDate).toISOString(),
            documentType: data.documentType,
            signingStatus: data.signingStatus === 21 ? 20 : data.signingStatus,
            tags: (data.tags || []).map(x => x.id).sort(),
            relations: (data.relations || []).map(x => x.id).sort(),
            isPublicOnPortal: data.isPublicOnPortal,
          },
          scannedDocument: null,

          isRelationRequired:
            showRelations &&
            (DataCacheService.getDefaultCompany()?.forceLinkingDocumentsToRelation ?? false),

          showRelations,
        },
        () => {
          let scannedDocumentPromise;
          let tasksPromise;
          if (data.scannedDocumentId) {
            scannedDocumentPromise = api.base.get(
              `${config.appUrls.documents}/api/documents/info/${data.scannedDocumentId}`,
            );
          } else {
            scannedDocumentPromise = new Promise(resolve => resolve({ data: null }));
          }

          if (data.links && data.links.length && data.links.some(x => x.linkType === 5)) {
            const taskIds = data.links.filter(x => x.linkType === 5).map(x => x.linkId);
            tasksPromise = api.base.post(`${config.appUrls.tasks}/api/plannedtasks/tasks`, taskIds);
          } else {
            tasksPromise = new Promise(resolve => resolve({ data: null }));
          }

          Promise.all([scannedDocumentPromise, tasksPromise]).then(
            ([{ data: scannedDocument }, { data: tasks }]) => {
              this.setState({ scannedDocument, tasks });
            },
          );
        },
      );
    });
  };

  getAvailableTags = () => {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.documents}/api/Tag`).then(
      ({ data }) => {
        this.setState({
          tags: data,
        });
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  documentDateChanged = documentDate => {
    const { documentDetails } = this.state;

    this.setState({
      documentDetails: {
        ...documentDetails,
        documentDate,
      },
    });
  };

  navigatePreviousDocument = () => {
    const { documentDetails } = this.state;
    const { getPreviousDocumentId } = this.props;
    const previousDocumentId = getPreviousDocumentId(documentDetails.id);
    this.navigateDocument(previousDocumentId);
  };

  navigateNextDocument = () => {
    const { documentDetails } = this.state;
    const { getNextDocumentId } = this.props;
    Promise.resolve(getNextDocumentId(documentDetails.id)).then(nextDocumentId => {
      this.navigateDocument(nextDocumentId);
    });
  };

  navigateDocument = documentId => {
    if (documentId) {
      this.getDocumentDetails(documentId);
    }
  };

  saveDocumentDetails = () => {
    const { documentDetails, initialDocumentDetails } = this.state;
    const { intl } = this.props;
    const model = { id: documentDetails.id };

    if (documentDetails.name !== initialDocumentDetails.name) {
      model.name = documentDetails.name;
    }
    if (!documentDetails.documentDate.isSame(moment.utc(initialDocumentDetails.documentDate))) {
      model.documentDate = documentDetails.documentDate;
    }
    if (documentDetails.documentType !== initialDocumentDetails.documentType) {
      model.documentType = documentDetails.documentType;
    }
    // Use == because <select> onChange produces strings, API returns numbers
    if (documentDetails.signingStatus != initialDocumentDetails.signingStatus) {
      model.signingStatus = documentDetails.signingStatus;
    }
    const currentTagIds = (documentDetails.tags || []).map(x => x.id).sort();
    if (!isEqual(currentTagIds, initialDocumentDetails.tags)) {
      model.tags = documentDetails.tags;
    }
    const currentRelationIds = (documentDetails.relations || []).map(x => x.id).sort();
    if (!isEqual(currentRelationIds, initialDocumentDetails.relations)) {
      model.relations = documentDetails.relations;
    }
    if (documentDetails.isPublicOnPortal !== initialDocumentDetails.isPublicOnPortal) {
      model.isPublicOnPortal = documentDetails.isPublicOnPortal;
    }

    return api.base.put(`${config.appUrls.documents}/api/documents/updateDetails`, model).then(
      ({ data }) => data,
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  updateDocument = () => {
    const { onSave } = this.props;
    return this.saveDocumentDetails().then(data => onSave(data));
  };

  addTagToDocument = tagName => {
    if (!tagName) return;
    const { documentDetails } = this.state;
    const currentDocumentTagNames = documentDetails.tags.map(x => x.name);

    if (currentDocumentTagNames.includes(tagName)) return;

    this.findOrCreateTag(tagName).then(tag => {
      this.setState({
        documentDetails: {
          ...documentDetails,
          tags: [...documentDetails.tags, tag],
        },
      });
    });
  };

  onRemoveTag = tag => {
    const { documentDetails } = this.state;

    this.setState({
      documentDetails: {
        ...documentDetails,
        tags: documentDetails.tags.filter(x => x.id !== tag.id),
      },
    });
  };

  findOrCreateTag = tagName =>
    new Promise((resolve, reject) => {
      const { tags } = this.state;
      const tag = tags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
      if (tag) {
        resolve(tag);
      } else {
        api.base.post(`${config.appUrls.documents}/api/Tag`, { name: tagName }).then(({ data }) => {
          if (!data) return reject();
          this.setState(
            state => ({
              tags: [...state.tags, data],
            }),
            () => resolve(data),
          );
        }, reject);
      }
    });

  onFilenameChanged = filenameWithoutExtension => {
    const { documentDetails } = this.state;

    const extension = documentDetails.name.split('.').pop();

    const newFilename = `${filenameWithoutExtension}.${extension}`;

    this.setState({
      documentDetails: {
        ...documentDetails,
        name: newFilename,
      },
    });
  };

  getMetadataComponent = document => {
    if (!document || !document.metadataJson) return null;

    switch (document.documentType) {
      // Email document
      case 8:
      case 23:
        return (
          <EmailMetadataDetails
            {...document}
            onResendEmail={() => this.setState({ showResendMailConfirmation: true })}
            onForward={() => this.setState({ showForwardModal: true })}
            onDeleteAttachment={attachment => this.onDeleteAttachmentRequested(attachment)}
          />
        );
      default:
        return null;
    }
  };

  onDeleteAttachmentRequested = attachment => {
    this.setState({ activeAttachment: attachment, showRemoveAttachmentModal: true });
  };

  removeAttachment = () => {
    const { activeAttachment, documentDetails } = this.state;
    const { intl } = this.props;

    return api.base
      .delete(
        `${config.appUrls.documents}/api/emails/DeleteAttachment/${documentDetails.emailId}/${activeAttachment.id}`,
      )
      .then(() => {
        this.setState(state => ({
          showRemoveAttachmentModal: false,
          documentDetails: {
            ...state.documentDetails,
            attachments: state.documentDetails.attachments.filter(
              x => x.id !== activeAttachment.id,
            ),
          },
        }));
        ToastrService.success(intl.formatMessage(messages.Success));
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  resendEmail = docId => {
    if (!docId) return;
    const { intl } = this.props;

    return api.base.post(`${config.appUrls.documents}/api/emails/Resend/${docId}`).then(
      ({ data }) => {
        if (!data) return;
        this.setState({
          showResendMailConfirmation: false,
        });
        ToastrService.success(intl.formatMessage(messages.TheMailHasBeenSentAgain));
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  addRelation = relation => {
    if (relation && relation.id) {
      this.setState(state => ({
        documentDetails: {
          ...state.documentDetails,
          relations: [
            ...state.documentDetails.relations.filter(x => x.id !== relation.id),
            relation,
          ],
        },
      }));
    }
  };
  removeRelation = relation => {
    if (relation && relation.id) {
      this.setState(state => ({
        documentDetails: {
          ...state.documentDetails,
          relations: state.documentDetails.relations.filter(x => x.id !== relation.id),
        },
      }));
    }
  };

  // debounce makes sure that if this method is called again within 300 ms, only the last call is executed.
  // this method adds the tag automatically to the document when it is found in the list of tags
  // this is needed because if there are two tags: test and test2, and the user starts typing, test2 will never be able
  // to be added to the document because the value that was entered will me immediately processed and found
  debouncedFindAndAddTag = debounce(value => {
    const { tags } = this.state;
    if (value && tags.find(x => x.name === value)) {
      this.addTagToDocument(value);
      this.setState({ newTag: '' });
    }
  }, 400);

  onTagValueChanged = value => {
    this.setState({ newTag: value });
    this.debouncedFindAndAddTag(value);
  };

  getSigningStatusClassName = status => {
    const className = classNames('fa', {
      'fa-circle-o': status === 1,
      'fa-circle text-orange': status === 2,
      'fa-stop-circle': status === 3,
      'fa-clock-o text-danger': status === 4,
      'fa-circle text-blue': status === 10,
      'fa-times-circle text-danger': status === 11,
      'fa-minus-circle text-danger': status === 12,
      'fa-check-circle text-success': status === 20 || status === 21,
    });
    return className;
  };
  // This method can return multiple tasks because multiple tasks could have been opened after another
  // (clicking parent or subtask -> vsts-like)
  onTaskDetailModalClosed = updatedTasks => {
    const { tasks } = this.state;
    const tasksCopy = [...tasks];

    if (updatedTasks) {
      updatedTasks.map(task => {
        const index = tasksCopy.findIndex(x => x.id === task.id);

        // task is not visible in this list
        if (index < 0) return;

        tasksCopy[index] = task;
      });
    }

    this.setState(state => ({
      ...state,
      showTaskDetailsModal: false,
      tasks: tasksCopy,
    }));
  };

  selectedDocumentTypeChanged = item => {
    if (item === undefined) return;
    const { documentDetails } = this.state;

    this.setState({
      documentDetails: {
        ...documentDetails,
        documentType: item ? item.key : undefined,
      },
    });
  };

  handleTasksUpdated = updatedTasks => {
    if (!updatedTasks || !updatedTasks.length) return;
    this.setState(({ tasks }) => ({
      tasks: tasks.map(task => {
        const updatedTask = updatedTasks.find(x => x.id === task.id);
        if (!updatedTasks) return task;
        return { ...task, ...updatedTask };
      }),
    }));
  };
  onToDoCreated = todo => {
    this.setState(({ tasks }) => {
      if (!todo || !todo.id) return { showCreateTodoModal: false };

      return { showCreateTodoModal: false, tasks: [...(tasks || []), todo] };
    });
  };
  showValidation = () => {
    this.setState({ showValidation: true });
  };
  isFormInvalid = () => {
    const { documentDetails, isRelationRequired } = this.state;
    if (!documentDetails) return true;
    if (!documentDetails.documentDate) return true;
    if (!validateMinimumDate(documentDetails.documentDate)) return true;
    if (isRelationRequired && (documentDetails.relations?.length ?? 0) === 0) return true;

    return false;
  };

  showAddTasks = () => this.setState({ addTasks: true });
  hideAddTasks = () => this.setState({ addTasks: false });
  linkToTask = task => {
    if (!task || !task.id) return;
    const { intl } = this.props;
    const { document, tasks: currentTasks } = this.state;
    if (currentTasks && currentTasks.some(x => x.id === task.id)) return; // Already linked

    const taskToAdd = {
      id: task.id,
      taskLevel: task.isToDo ? 2 : 0,
      isBlocked: task.isBlocked || task.status === 3,
      inapplicable: task.inapplicable || task.status === 4,
      rejected: task.rejected || task.status === 5,
      status: Math.min(task.status, 2),
      name: task.plannedTaskName,
      templateName: task.plannedTaskTemplateName,
      templateId: task.plannedTaskTemplateId,
      deadline: task.deadline,
    };

    return api.base
      .post(`${config.appUrls.documents}/api/documents/Links`, {
        documentId: document.id,
        linkId: task.id,
        linkType: 5,
        removeExistingRelations: false,
        recognizeTemporaryDocuments: false,
      })
      .then(() => {
        this.setState(({ tasks }) => ({ tasks: [...(tasks || []), taskToAdd] }));
      }, handleHttpError(intl));
  };
  showDeleteTaskLink = task => this.setState({ deleteTaskLink: task });
  deleteTaskLink = () => {
    const { intl } = this.props;
    const { deleteTaskLink, document } = this.state;
    return api.base
      .delete(
        `${config.appUrls.documents}/api/documents/Links/${document.id}/${deleteTaskLink.id}/5`,
      )
      .then(() => {
        this.setState(({ tasks }) => ({
          tasks: (tasks || []).filter(x => x.id !== deleteTaskLink.id),
          deleteTaskLink: null,
        }));
      }, handleHttpError(intl));
  };

  sourceFormatted = source => {
    const { intl } = this.props;
    const translated = formatListValue({ id: source, list: 'entitysourcetype', intl });

    if (!translated.includes('API')) {
      return `${translated.charAt(0).toLowerCase()}${translated.slice(1)}`;
    }

    return translated;
  };

  replaceDocumentInternal = (document, files) => {
    const { onReplaceDocument } = this.props;
    var replaceMethod = onReplaceDocument ? onReplaceDocument : this.defaultReplaceDocument;

    return replaceMethod(document, files).then(
      updatedDocument => {
        if (!updatedDocument) {
          return;
        }
        this.setState(
          state => ({
            documentDetails: {
              ...state.documentDetails,
              name: updatedDocument.name,
            },
            initialDocumentDetails: {
              ...state.initialDocumentDetails,
              name: updatedDocument.name,
            },
            document: {
              ...state.document,
              name: updatedDocument.name,
              mime: updatedDocument.mime,
            },
          }),
          () => this.getDocumentLink(document.id),
        );
      },
      error => {
        const { intl } = this.props;
        handleHttpError(intl)(error);
      },
    );
  };

  defaultReplaceDocument = (document, [file]) => {
    if (!file || !document || !document.canBeReplaced) return;

    const formData = new FormData();
    formData.append('documentId', document.id);
    formData.append('data', file);

    return api.base
      .post(`${config.appUrls.documents}/api/Documents/Replace`, formData, {
        headers: {
          'Content-Type': undefined,
        },
      })
      .then(
        ({ data }) => {
          if (!data) return;
          ToastrService.success();
          return data;
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      );
  };

  checkinDocument(document) {
    const { intl, onCheckinCheckout } = this.props;
    DocumentService.checkin(document.id).then(
      () => {
        document.checkedOutByUserId = null;
        this.setState({ document });
        if (!!onCheckinCheckout) {
          onCheckinCheckout(document);
        }
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        }
        log.error('error checking in document ', error);
      },
    );
  }

  checkoutDocument = (document, openinApp) => {
    if (!document || !document.canEdit) return;
    const { intl, onCheckinCheckout } = this.props;
    DocumentService.checkout(document.id, openinApp).then(
      data => {
        if (!!data) {
          document.checkedOutByUserId = authService.getUserId();
          this.setState({ document });
          if (!!onCheckinCheckout) {
            onCheckinCheckout(document);
          }
        }
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        } else {
          ToastrService.error(intl.formatMessage(messages.MsgCheckoutDocumentSuccess));
        }
        log.error('error checking out document ', error);
      },
    );
  };

  discardCheckout = document => {
    const { intl } = this.props;
    DocumentService.discardCheckout(document.id).then(
      () => {
        document.checkedOutByUserId = null;
        this.setState({ showCheckinDocumentModal: false, document });
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        }
        this.setState({ showCheckinDocumentModal: false });
        log.error('error checking in document ', error);
      },
    );
  };

  render() {
    const {
      show,
      onClose,
      disabled,
      intl,
      allowNavigation,
      onRemoveDocument,
      onShareItem,
      forceRelationMode,
      checkedoutReminderMode,
    } = this.props;
    const {
      documentLink,
      loading,
      documentDetails,
      document,
      dateFocused,
      tags,
      newTag,
      scannedDocument,
      tasks,
      showTaskDetailsModal,
      activeTaskId,
      selectedDocumentType,
      showRemoveAttachmentModal,
      showCreateTodoModal,
      documentTypes,
      fullScreen,
      showValidation,
      showForwardModal,
      showResendMailConfirmation,
      addTasks,
      deleteTaskLink,
      showReplyToEmailModal,
      replyAll,
      unauthorized,
      showShareItemModal,
      isRelationRequired,
      showRelations,
      showAuditLogs,
      showCheckinDocumentModal,
      extractingData,
      showExtractDataConfirmModal,
    } = this.state;
    const filenameWithoutExtension =
      documentDetails && documentDetails.name && documentDetails.name.replace(/\.[^/.]+$/, '');
    const extension =
      documentDetails && documentDetails.name && documentDetails.name.split('.').pop();

    const filenameClassName = classNames('form-group', {
      'has-error': !filenameWithoutExtension && !loading,
    });

    const metadataComponent = this.getMetadataComponent(documentDetails);

    const disableRelationPicker = disabled || !document || !document.canRemove;

    const canBeChangedToDocumentTypes = !documentDetails.cannotBeChangedTo
      ? []
      : documentTypes.filter(x => !documentDetails.cannotBeChangedTo.includes(x.id));

    const canEdit = authService.hasPermission({ functionality: 'Documents', privilege: 'Update' });
    const canCreateTags = authService.hasPermission({ functionality: 'Tags', privilege: 'Create' });
    const canCreateTodo = authService.hasPermission({ functionality: 'Todo', privilege: 'Create' });

    const canDelete =
      document &&
      document.canRemove &&
      authService.hasPermission({ functionality: 'Documents', privilege: 'Delete' });
    const canBeReplaced =
      document &&
      document.canBeReplaced &&
      authService.hasPermission({ functionality: 'Documents', privilege: 'Update' });
    const isPdf = document && document.mime === 'application/pdf';

    return (
      <Modal
        show={show}
        onHide={onClose}
        dialogClassName={classNames('document-details-modal', {
          'full-screen': fullScreen,
          'modal-xl': !fullScreen,
        })}
      >
        <Modal.Header closeButton>
          <button
            type="button"
            className="toggle-full-screen"
            onClick={() => this.setState(state => ({ fullScreen: !state.fullScreen }))}
          >
            <i
              className={classNames('fa', {
                'fa-compress': fullScreen,
                'fa-expand': !fullScreen,
              })}
            />
          </button>
          <button
            type="button"
            className="toggle-audit-logs"
            onClick={() => this.setState(state => ({ showAuditLogs: !state.showAuditLogs }))}
          >
            <i
              title={
                showAuditLogs
                  ? intl.formatMessage(messages.HideAuditLogs)
                  : intl.formatMessage(messages.ShowAuditLogs)
              }
              className="fa fa-history"
            />
          </button>
          {document && (
            <Modal.Title>
              {showAuditLogs ? (
                <>
                  {intl.formatMessage(messages.AuditLogsForDocument, { name: document.name })}
                  <ZendeskHelp article="15983427107740" />
                </>
              ) : (
                <>
                  <FileIcon document={document} />
                  &nbsp; {document.name}
                </>
              )}
            </Modal.Title>
          )}
        </Modal.Header>
        <Modal.Body>
          {showShareItemModal && (
            <ShareItemModal
              show
              type="document"
              id={document.id}
              itemName={document.name}
              relationId={
                document.relations && document.relations.length > 0
                  ? document.relations[0].id
                  : undefined
              }
              relationName={
                document.relations && document.relations.length > 0
                  ? getRelationName(intl, document.relations[0])
                  : undefined
              }
              onClose={() => this.setState({ showShareItemModal: false })}
            />
          )}
          {showResendMailConfirmation && (
            <YesNoModal
              show
              title={intl.formatMessage(messages.ResendEmail)}
              message={intl.formatMessage(messages.ResendEmailConfirmationMsg)}
              onNo={() => this.setState({ showResendMailConfirmation: false })}
              onSave={() => this.resendEmail(document.id)}
            />
          )}
          {showCheckinDocumentModal && (
            <YesNoModal
              show
              title={intl.formatMessage(messages.DiscardCheckout)}
              message={intl.formatMessage(messages.CheckinDocumentConfirmationMsg, {
                name: DataCacheService.getUserName(document.checkedOutByUserId),
              })}
              onNo={() => this.setState({ showCheckinDocumentModal: false })}
              onSave={() => this.discardCheckout(document)}
            />
          )}
          {showExtractDataConfirmModal && (
            <YesNoModal
              show={showExtractDataConfirmModal}
              title={intl.formatMessage(documentMessages.ConfirmExtractDataWithAI)}
              message={intl.formatMessage(documentMessages.ConfirmExtractDataWithAIMessage)}
              onSave={dontShowAgain => this.confirmExtractDataWithAI(dontShowAgain)}
              onNo={this.cancelExtractDataWithAI}
              onClose={this.cancelExtractDataWithAI}
              showDontShowAgain={true}
            />
          )}
          {showForwardModal && (
            <ForwardEmailModal
              intl={intl}
              documentId={document.id}
              relationId={
                documentDetails.relations && documentDetails.relations.length === 1
                  ? documentDetails.relations[0].id
                  : null
              }
              show
              onClose={() =>
                this.setState(state => ({
                  ...state,
                  showForwardModal: false,
                }))
              }
            />
          )}
          {showReplyToEmailModal && (
            <ReplyToEmailModal
              intl={intl}
              replyAll={replyAll}
              documentId={document.id}
              relationId={
                documentDetails.relations && documentDetails.relations.length === 1
                  ? documentDetails.relations[0].id
                  : null
              }
              show
              onClose={() => this.setState({ showReplyToEmailModal: false })}
            />
          )}
          {showTaskDetailsModal && (
            <TaskDetailsModal
              show
              task={{ id: activeTaskId }}
              onSave={this.onTaskDetailModalClosed}
              onClose={this.onTaskDetailModalClosed}
            />
          )}
          {showRemoveAttachmentModal && (
            <YesNoModal
              show
              title={intl.formatMessage(messages.DeleteAttachment)}
              message={intl.formatMessage(messages.DeleteAttachmentConfirmationMsg)}
              onNo={() => this.setState({ showRemoveAttachmentModal: false })}
              onSave={() => this.removeAttachment()}
            />
          )}
          {showCreateTodoModal && (
            <CreateToDoModal
              show
              documents={[document]}
              relationId={
                documentDetails.relations && documentDetails.relations.length > 0
                  ? documentDetails.relations[0].id
                  : undefined
              }
              onSave={this.onToDoCreated}
              onClose={() => this.setState({ showCreateTodoModal: false })}
            />
          )}
          {deleteTaskLink && (
            <YesNoModal
              show
              title={intl.formatMessage(messages.DeleteTaskLink)}
              message={intl.formatMessage(messages.DeleteTaskLinkConfirmationMsg)}
              onNo={() => this.setState({ deleteTaskLink: null })}
              onSave={() => this.deleteTaskLink()}
            />
          )}
          {forceRelationMode && isRelationRequired ? (
            <div className="alert alert-warning">
              <i className="fa fa-exclamation-triangle text-warning margin5R" aria-hidden="true" />
              <FormattedMessage {...messages.ForceRelationModeInfo} />
            </div>
          ) : null}
          {checkedoutReminderMode ? (
            <div className="alert alert-warning">
              <i className="fa fa-exclamation-triangle text-warning margin5R" aria-hidden="true" />
              <FormattedMessage {...messages.CheckedOutReminderModeInfo} />
            </div>
          ) : null}
          <div className="content-wrapper">
            {showAuditLogs && document && (
              <DocumentAudits
                documentId={document.id}
                storageType={document.storageType}
                creationDate={document.creationDate}
              />
            )}
            <div className="preview" style={{ display: showAuditLogs ? 'none' : 'flex' }}>
              <div
                className={classNames('pg-viewer-wrapper', {
                  pdf: isPdf,
                })}
              >
                <div className="pg-viewer" id="pg-viewer">
                  {loading && <LoadingAnimation />}
                  {!loading &&
                    !!document &&
                    isValidNumber(document.documentType) &&
                    !!documentLink &&
                    !unauthorized && (
                      <FileViewer
                        documentType={document.documentType}
                        documentId={document.id}
                        mimeType={document.mime}
                        filePath={documentLink}
                        fileName={document.name}
                        storageType={document.storageType}
                      />
                    )}
                  {!loading &&
                    !!document &&
                    isValidNumber(document.documentType) &&
                    unauthorized && <FormattedMessage {...messages.FileUnauthorized} />}
                </div>
              </div>
            </div>
            <div className="document-info">
              {loading && <LoadingAnimation />}

              {!loading && document && documentDetails && documentDetails.id && (
                <form ref={this.form}>
                  <div className="form-horizontal">
                    <div className={filenameClassName}>
                      <label className="col-sm-4 col-md-4">
                        <FormattedMessage {...messages.Filename} />
                      </label>
                      <div className="col-sm-6 col-md-8">
                        <div className="input-group ">
                          <input
                            type="text"
                            disabled={!canEdit}
                            className="form-control"
                            value={filenameWithoutExtension}
                            onChange={e =>
                              this.onFilenameChanged(
                                e.target.value.replace('/', '').replace('\\', ''),
                              )
                            }
                            required
                          />
                          <div className="input-group-addon">.{extension}</div>
                        </div>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="col-sm-4 col-md-4">
                        <FormattedMessage {...messages.CreationDate} />
                      </label>
                      <div className="col-sm-6 col-md-8">
                        <p className="form-control-static">
                          <FormattedDateTime value={document.creationDate} />
                          {documentDetails.source > 0 && (
                            <span>
                              &nbsp;via&nbsp;
                              {this.sourceFormatted(documentDetails.source)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {documentDetails.creationUser ? (
                      <div className="form-group">
                        <label className="col-sm-4 col-md-4">
                          <FormattedMessage {...messages.DocumentCreatedBy} />
                        </label>
                        <div className="col-sm-6 col-md-8">
                          <p className="form-control-static">{documentDetails.creationUser}</p>
                        </div>
                      </div>
                    ) : null}

                    <div
                      className={classNames('form-group', {
                        'has-error': showValidation && !documentDetails.documentDate,
                      })}
                    >
                      <label className="col-sm-4 col-md-4 ">
                        <FormattedMessage {...messages.DocumentDate} />
                      </label>
                      <div className="col-sm-6 col-md-8">
                        <DatePicker
                          placeholder={intl.formatMessage(messages.DocumentDate)}
                          date={documentDetails.documentDate}
                          onDateChange={this.documentDateChanged}
                          focused={dateFocused}
                          disabled={!canEdit}
                          onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                          id="documentDate"
                          required
                          block
                          small
                          isOutsideRange={isBeforeMinimum}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="col-sm-4 col-md-4">
                        <FormattedMessage {...messages.DocumentType} />
                      </label>
                      <div className="col-sm-6 col-md-8 document-type">
                        {documentDetails.canRemove && canBeChangedToDocumentTypes.length > 0 ? (
                          <AutoComplete
                            disabled={!canEdit}
                            items={canBeChangedToDocumentTypes}
                            placeholder={intl.formatMessage(messages.StartTypingToSearch)}
                            onChange={item => this.selectedDocumentTypeChanged(item)}
                            mode="edit"
                            value={selectedDocumentType}
                            showAll
                            required
                            menuRight
                          />
                        ) : (
                          <FormattedListValue
                            list="documentType"
                            id={documentDetails.documentType}
                          />
                        )}
                      </div>
                    </div>

                    {(documentDetails.canBeSigned || document.signingStatus >= 10) && (
                      <div className="form-group">
                        <label className="col-sm-4 col-md-4">
                          <FormattedMessage {...messages.Signature} />
                        </label>
                        <div className="col-sm-6 col-md-8">
                          <select
                            disabled={!canEdit}
                            className="form-control"
                            onChange={e => {
                              const status = e.target.value;
                              this.setState(state => ({
                                documentDetails: {
                                  ...state.documentDetails,
                                  signingStatus: status === '' ? null : status,
                                },
                              }));
                            }}
                            value={documentDetails.signingStatus || ''}
                          >
                            <option value="0"> {}</option>
                            <option value="1">
                              {intl.formatMessage(listItemMsg('signingStatus', '1'))}
                            </option>
                            <option value="2">
                              {intl.formatMessage(listItemMsg('signingStatus', '2'))}
                            </option>
                            <option value="3">
                              {intl.formatMessage(listItemMsg('signingStatus', '3'))}
                            </option>
                            <option value="4">
                              {intl.formatMessage(listItemMsg('signingStatus', '4'))}
                            </option>
                            <option value="10">
                              {intl.formatMessage(listItemMsg('signingStatus', '10'))}
                            </option>
                            <option value="11">
                              {intl.formatMessage(listItemMsg('signingStatus', '11'))}
                            </option>
                            <option value="12">
                              {intl.formatMessage(listItemMsg('signingStatus', '12'))}
                            </option>
                            <option value="20">
                              {intl.formatMessage(listItemMsg('signingStatus', '20'))}
                            </option>
                          </select>
                          {documentDetails.signingFailedReason && (
                            <div>{documentDetails.signingFailedReason}</div>
                          )}
                        </div>
                      </div>
                    )}
                    {showRelations ? (
                      <div
                        className={classNames('form-group', {
                          'has-error':
                            showValidation &&
                            isRelationRequired &&
                            (documentDetails.relations?.length ?? 0) === 0,
                        })}
                      >
                        <label className="col-sm-4 col-md-4">
                          <FormattedMessage {...messages.Relations} />
                        </label>
                        <div className="col-sm-6 col-md-8">
                          <RelationListPicker
                            relations={documentDetails.relations || []}
                            onAddRelation={this.addRelation}
                            onRemoveRelation={this.removeRelation}
                            disabled={disableRelationPicker || !canEdit}
                          />
                        </div>
                      </div>
                    ) : null}
                    {scannedDocument && (
                      <div className="form-group">
                        <label className="col-sm-4 col-md-4">
                          <FormattedMessage {...messages.ScannedDocument} />
                        </label>
                        <div className="col-md-8 col-sm-6">
                          <span className="inline-email-attachment" style={{ padding: '0' }}>
                            <span>
                              <a
                                className="pull-right"
                                onClick={() => DocumentService.open(scannedDocument.id)}
                                title={intl.formatMessage(messages.Download)}
                              >
                                <DownloadIcon />
                              </a>
                              <a
                                onClick={() => this.navigateDocument(scannedDocument.id)}
                                title={intl.formatMessage(messages.Open)}
                              >
                                <FileIcon document={scannedDocument} />
                                {scannedDocument.name}
                              </a>
                            </span>
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="form-group">
                      <label className="col-sm-4 col-md-4">
                        <FormattedMessage {...messages.Tags} />
                      </label>
                      <div className="col-sm-6 col-md-8">
                        {documentDetails && tags && (
                          <MultiSelect
                            placeholder={intl.formatMessage(messages.EnterTag)}
                            disabled={!canEdit}
                            onItemSelected={x => this.addTagToDocument(x?.name)}
                            items={tags.map(x => ({ ...x, label: x.name }))}
                            selectedItem={
                              documentDetails.tags?.map(x => ({ ...x, label: x.name })) ?? []
                            }
                            onCreateItem={canCreateTags ? this.addTagToDocument : undefined}
                            onItemRemoved={x => this.onRemoveTag(x.value)}
                          />
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="col-sm-4 col-md-4 text-nowrap">
                        <FormattedMessage {...messages.IsPublicOnPortal} />
                        <OverlayTrigger
                          rootClose
                          trigger={['hover', 'focus']}
                          placement="bottom"
                          overlay={
                            <Popover id="popover-trigger-hover-focus">
                              {intl.formatMessage(messages.DocumentIsPublicOnPortalExplanation)}
                            </Popover>
                          }
                        >
                          <span className="margin5L explanation-icon">
                            <FaInfoCircle />
                          </span>
                        </OverlayTrigger>
                      </label>
                      <div className="col-sm-6 col-md-8 margin5T">
                        <Checkbox
                          disabled={!canEdit}
                          onClick={() =>
                            this.setState({
                              documentDetails: {
                                ...documentDetails,
                                isPublicOnPortal: !documentDetails.isPublicOnPortal,
                              },
                            })
                          }
                          value={documentDetails.isPublicOnPortal}
                        />
                      </div>
                    </div>

                    {metadataComponent !== null && (
                      <div className="border-top margin10T">{metadataComponent}</div>
                    )}
                    <div className="form-group">
                      <label className="col-sm-4 col-md-4">
                        <b>
                          <FormattedMessage {...messages.Tasks} />
                        </b>
                      </label>
                      <div className="col-sm-6 col-md-8">
                        {tasks && (
                          <table className="margin10B width100pct">
                            <tbody>
                              {tasks.map(task => (
                                <tr key={task.id}>
                                  <td className="status-column">
                                    <TaskStatusToggle
                                      id={`TaskStatusToggle--${task.id}`}
                                      value={{
                                        id: task.id,
                                        status: task.status,
                                        isBlocked: task.isBlocked,
                                        inapplicable: task.inapplicable,
                                        rejected: task.rejected,
                                        taskLevel: task.taskLevel,
                                      }}
                                      isMainTask={task.taskLevel !== 1}
                                      onTasksUpdated={this.handleTasksUpdated}
                                      onOpenDetail={taskid =>
                                        this.setState({
                                          showTaskDetailsModal: true,
                                          activeTaskId: taskid,
                                        })
                                      }
                                    />
                                  </td>
                                  <td className="taskname-column">
                                    <span
                                      className={classNames('text-clickable', {
                                        strike: task.inapplicable || task.rejected,
                                      })}
                                      title={intl.formatMessage(messages.ViewTask)}
                                      onClick={() =>
                                        this.setState({
                                          activeTaskId: task.id,
                                          showTaskDetailsModal: true,
                                        })
                                      }
                                    >
                                      <TaskIcon taskLevel={task.taskLevel} />
                                      {task.name && task.name}
                                      {!task.name && (
                                        <FormattedListValue
                                          list="tasktemplate"
                                          id={task.templateId}
                                          fallback={
                                            task.name || task.templateName || task.templateId
                                          }
                                        />
                                      )}
                                      {task.deadline ? (
                                        <span>
                                          &nbsp; (<FormattedDateShort value={task.deadline} />)
                                        </span>
                                      ) : null}
                                    </span>
                                  </td>
                                  {canEdit ? (
                                    <td>
                                      <i
                                        onClick={() => this.showDeleteTaskLink(task)}
                                        title={intl.formatMessage(messages.DeleteTaskLink)}
                                        className="fa fa-trash-o clickable margin10L"
                                      />
                                    </td>
                                  ) : null}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {addTasks && documentDetails.relations.length > 0 ? (
                          <div>
                            <OutsideClickHandler onOutsideClick={this.hideAddTasks}>
                              <TasksForMultipleRelationsSelector
                                onSelect={this.linkToTask}
                                relations={documentDetails.relations.map(x => x.id)}
                                autoFocus
                              />
                            </OutsideClickHandler>
                          </div>
                        ) : null}
                        {canEdit && !addTasks && documentDetails.relations.length > 0 ? (
                          <Button
                            btnClassName="btn-sm btn-block margin5B"
                            onClick={this.showAddTasks}
                          >
                            <span className="margin5R react-icon">
                              <FaLink />
                            </span>
                            {intl.formatMessage(messages.LinkToTask)}
                          </Button>
                        ) : null}
                        {canCreateTodo ? (
                          <Button
                            btnClassName="btn-sm btn-block"
                            onClick={() => this.setState({ showCreateTodoModal: true })}
                          >
                            <span className="margin5R">
                              <AddIcon />
                            </span>
                            {intl.formatMessage(messages.CreateNewToDo)}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div style={{ float: 'left', width: '80%' }}>
            {allowNavigation && (
              <Button onClick={this.navigatePreviousDocument}>
                <LeftArrowIcon className="margin5R" />{' '}
                {intl.formatMessage(messages.PreviousDocument)}
              </Button>
            )}
            {allowNavigation && (
              <Button onClick={this.navigateNextDocument}>
                <RightArrowIcon className="margin5R" /> {intl.formatMessage(messages.NextDocument)}
              </Button>
            )}
            {document &&
              authService.hasFeature('AI') &&
              authService.hasPermission({
                functionality: functionalities.AI,
                privilege: privileges.Update,
              }) &&
              DocumentService.supportsDataExtraction(documentDetails.documentType) &&
              !document.name?.toLowerCase().endsWith('.eid') &&
              documentDetails.relations?.length > 0 && (
                <Button
                  onClick={this.extractDocumentData}
                  disabled={extractingData}
                  showProgress
                  title={
                    extractingData
                      ? intl.formatMessage(messages.ExtractionInProgress)
                      : intl.formatMessage(messages.ExtractDocumentData)
                  }
                >
                  {extractingData ? (
                    <>
                      <SpinnerIcon className="margin5R" />
                      {intl.formatMessage(messages.ExtractingDocumentData)}
                    </>
                  ) : (
                    <>
                      <i className="fa fa-magic margin5R" />
                      {intl.formatMessage(messages.ExtractDocumentData)}
                    </>
                  )}
                </Button>
              )}
            {document && DocumentService.canConvertToPdf(document.mime) && (
              <Button
                onClick={() => DocumentService.convertToPdf(document.id, document.name)}
                showProgress
              >
                <FaFilePdf className="text-danger margin5R" />
                {intl.formatMessage(messages.DownloadAsPdf)}
              </Button>
            )}
            {document && (document.documentType === 8 || document.documentType === 23) ? (
              <>
                <Button
                  onClick={() => this.setState({ showReplyToEmailModal: true, replyAll: false })}
                >
                  <MdReply className="margin5R" /> {intl.formatMessage(messages.Reply)}
                </Button>
                <Button
                  onClick={() => this.setState({ showReplyToEmailModal: true, replyAll: true })}
                >
                  <MdReplyAll className="margin5R" /> {intl.formatMessage(messages.ReplyAll)}
                </Button>
              </>
            ) : null}

            <Button onClick={() => DocumentService.download(document.id)} showProgress>
              <DownloadIcon className="margin5R" /> {intl.formatMessage(messages.Download)}
            </Button>
            <Button onClick={() => this.setState({ showShareItemModal: true })} showProgress>
              <ShareIcon className="vertical-align-middle margin5R" />
              {intl.formatMessage(messages.Share)}
            </Button>
            {document &&
              document.canEdit &&
              canEdit &&
              !DataCacheService.hasUserConnectPlatform(46) && (
                <Button
                  onClick={() => DocumentService.edit(document.id, intl.locale.split('-')[0])}
                  showProgress
                >
                  <IconPencil className="margin5R" /> {intl.formatMessage(messages.Edit)}
                </Button>
              )}

            {document &&
              document.canEdit &&
              canEdit &&
              DataCacheService.hasUserConnectPlatform(46) &&
              document.storageType !== 1 &&
              document.checkedOutByUserId == null && (
                <Button onClick={() => this.checkoutDocument(document, false)} showProgress>
                  <IconPencil className="margin5R" /> {intl.formatMessage(messages.CheckoutBrowser)}
                </Button>
              )}
            {document &&
              document.canEdit &&
              canEdit &&
              DataCacheService.hasUserConnectPlatform(46) &&
              document.storageType !== 1 &&
              document.checkedOutByUserId == null && (
                <Button onClick={() => this.checkoutDocument(document, true)} showProgress>
                  <IconPencil className="margin5R" /> {intl.formatMessage(messages.CheckoutApp)}
                </Button>
              )}
            {document &&
              document.canEdit &&
              canEdit &&
              DataCacheService.hasUserConnectPlatform(46) &&
              document.storageType !== 1 &&
              document.checkedOutByUserId != null &&
              document.checkedOutByUserId == authService.getUserId() && (
                <Button
                  onClick={() =>
                    DocumentService.getCheckedOutLink(document.id).then(link => {
                      if (!link) return;
                      window.open(link);
                    })
                  }
                  showProgress
                >
                  <IconPencil className="margin5R" /> {intl.formatMessage(messages.ContinueEditing)}
                </Button>
              )}
            {document &&
              document.canEdit &&
              canEdit &&
              DataCacheService.hasUserConnectPlatform(46) &&
              document.storageType !== 1 &&
              document.checkedOutByUserId != null && (
                <Button
                  onClick={() => this.checkinDocument(document)}
                  disabled={document.checkedOutByUserId != authService.getUserId()}
                  showProgress
                >
                  <SaveIcon className="margin5R" /> {intl.formatMessage(messages.Checkin)}
                </Button>
              )}
            {document &&
              document.canEdit &&
              canEdit &&
              (DataCacheService.hasUserConnectPlatform(46) ||
                authService.hasPermission({ functionality: 'CompanyOwn', privilege: 'Update' })) &&
              document.storageType !== 1 &&
              document.checkedOutByUserId != null && (
                <Button
                  onClick={() => this.setState({ showCheckinDocumentModal: true })}
                  disabled={
                    document.checkedOutByUserId != authService.getUserId() &&
                    !authService.hasPermission({ functionality: 'CompanyOwn', privilege: 'Update' })
                  }
                  showProgress
                >
                  <UndoIcon className="margin5R" /> {intl.formatMessage(messages.DiscardCheckout)}
                </Button>
              )}
            {document && canBeReplaced && (
              <SimpleFileUpload
                onUpload={files => this.replaceDocumentInternal(document, files)}
                className="margin5L"
                accept={document.mime}
                disabled={!!document.replacing}
              >
                {({ onClick }) => (
                  <Button
                    onClick={onClick}
                    showProgress
                    style={{ marginLeft: '0.4rem' }}
                    disabled={!!document.replacing}
                  >
                    <ReplaceIcon className="margin5R" /> {intl.formatMessage(messages.Replace)}
                  </Button>
                )}
              </SimpleFileUpload>
            )}
            {document && onRemoveDocument && document.canRemove && canDelete && (
              <Button onClick={() => onRemoveDocument(document)}>
                <DeleteIcon className="margin5R" /> {intl.formatMessage(messages.DeleteDocument)}
              </Button>
            )}
          </div>
          <Button onClick={onClose}>{intl.formatMessage(messages.Cancel)}</Button>
          <Button
            onClick={this.updateDocument}
            disabled={
              disabled ||
              !canEdit ||
              !filenameWithoutExtension ||
              (showValidation && this.isFormInvalid())
            }
            btnClassName="btn-inverse"
            onMouseOver={this.showValidation}
            onFocus={this.showValidation}
            showProgress
          >
            <SaveIcon className="margin5R" /> {intl.formatMessage(messages.Save)}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default injectIntl(DocumentDetailsModal);
